import type { CardTypeT } from '../Card.h'

import { styled } from '@linaria/react'
import { IconSC, IconSizes } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import { LabelStyled } from 'atoms/Label'
import { FIXED_INDENTS, MEDIA, SHADOW } from 'vars/index'
import { BaseCardSC, ImageBackgroundSC, PosterContainerSC } from '../base-card'
import {
  LockedPosterSC,
  TopEventSimpleCardBaseSC,
} from '../base-card/BaseCard.styled'
import {
  CARDS_ASPECT_RATIO,
  CardType,
  EVENT_CARD_ASPECT_RATIO,
} from '../Card.h'
import { TitleComponentSC } from '../Templates/MovieCardDefault/MovieCardTemplate.styled'

export const ContentLabelSC = styled.div<{ slidingLabel?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;

  ${LabelStyled} {
    position: absolute;
    right: var(${FIXED_INDENTS.INDENT_30});
    bottom: var(${FIXED_INDENTS.INDENT_30});
    max-width: ${({ slidingLabel }) =>
      slidingLabel
        ? `calc(100% - 2 * var(${FIXED_INDENTS.INDENT_30}))`
        : '100%'};
  }
`

/**
 * Live pill in the bottom-left poster corner, per the event card frame.
 * The 8px inset matches the other restyled corner labels (age rating group,
 * new-content slot), not the legacy 12px date label above.
 * Same stacking layer as the thumbnail fade so the pill paints above it.
 */
export const LiveLabelSC = styled.div`
  position: absolute;
  left: var(${FIXED_INDENTS.INDENT_20});
  bottom: var(${FIXED_INDENTS.INDENT_20});
  z-index: 2;
`

export const CardContentWrapperSC = styled.div<{
  rowGap?: FIXED_INDENTS
  posterAspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  gridCard?: boolean
  scaleOnHover?: boolean
}>`
  cursor: default;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${({ gridCard }) => (gridCard ? '100%' : 'auto')};
  row-gap: ${({ rowGap }) => `var(${rowGap})`};

  ${MEDIA.NO_TOUCH} {
    &:hover {
      ${BaseCardSC} {
        box-shadow: ${({ scaleOnHover }) =>
          scaleOnHover ? `var(${SHADOW.CARD})` : 'none'};
      }
    }
  }

  ${BaseCardSC} {
    height: ${({
      posterAspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
      gridCard,
    }) =>
      gridCard
        ? `calc(var(--cardWidth) / ${posterAspectRatio})`
        : `calc(var(--cardWidth) / var(--aspectRatio, ${posterAspectRatio}))`};
    width: 100%;

    ${PosterContainerSC} {
      ${TitleComponentSC} {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      ${ImageBackgroundSC} {
        ${PosterSC} {
          object-fit: cover;
        }

        ${LockedPosterSC} {
          ${IconSC} {
            font-size: ${IconSizes.xxmLarge};
          }
        }
      }
    }
  }
`

export const TopLiveEventPosterAreaSC = styled.div<{ type: CardTypeT }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ type }) =>
    `var(${
      type === CardType.VERTICAL_EVENT_TOP_CONTENT
        ? EVENT_CARD_ASPECT_RATIO.TOP_CONTENT_VERTICAL
        : EVENT_CARD_ASPECT_RATIO.TOP_CONTENT_HORIZONTAL
    })`};

  ${CardContentWrapperSC} {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: initial;
    aspect-ratio: ${({ type }) =>
      `var(${
        type === CardType.VERTICAL_EVENT_TOP_CONTENT
          ? EVENT_CARD_ASPECT_RATIO.VERTICAL_DEFAULT
          : EVENT_CARD_ASPECT_RATIO.HORIZONTAL_DEFAULT
      })`};

    ${BaseCardSC} {
      height: 100%;
      width: 100%;
    }
  }
`

export const TopLiveEventCardSC = styled(TopEventSimpleCardBaseSC)<{
  rowGap?: FIXED_INDENTS
}>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: ${({ rowGap = FIXED_INDENTS.INDENT_30 }) => `var(${rowGap})`};
`
